import { useState } from "react";
import { Globe, PanelLeft, Search, RefreshCw } from "lucide-react";
import { NotificationBell } from "./NotificationBell";
// ═ COMPANY-STRIP BEGIN ═ COMPANY-CUT-1 · 订阅版顶栏工具区（SAAS-2）
import { SubscriptionTools } from "./SubscriptionTools";
// ═ COMPANY-STRIP END ═
import { useNavigate } from "react-router-dom";
import { UserMenu } from "./UserMenu";
import { useAuthStore } from "@/store/authStore";
import { ROLE_LABEL, type Role } from "@/services/types/auth";
import { Breadcrumb } from "./Breadcrumb";

interface TopbarProps {
  onToggleSidebar?: () => void;
  /** 桌面端整条侧栏收起状态 + 切换（来自 Layout）*/
  desktopCollapsed?: boolean;
  onToggleDesktopSidebar?: () => void;
}

const ALL_ROLES: Role[] = [
  "tech_admin",
  "owner",
  "op_manager",
  "op_supervisor",
  "op",
  "finance",
  "account_manager",
  "tech_admin",
];

export function Topbar({
  onToggleSidebar: _onToggleSidebar,
  desktopCollapsed = false,
  onToggleDesktopSidebar,
}: TopbarProps) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const switchRole = useAuthStore((s) => s.switchRole);
  const [locale, setLocale] = useState<"zh" | "en">("zh");
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const [search, setSearch] = useState("");
  // 全局搜索（简单版）：回车 → 跳订单中心并按关键词后端筛
  const submitSearch = () => {
    const kw = search.trim();
    if (kw) navigate(`/orders?search=${encodeURIComponent(kw)}`);
  };

  // 仅 tech_admin / owner 可切角色（debug 用）
  const canSwitchRole =
    user?.role === "tech_admin" || user?.role === "owner";

  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-6"
    >
      <div className="flex items-center gap-2">
        {/* 桌面端侧栏收起/展开（隐藏侧栏 → 右侧全屏）*/}
        <button
          type="button"
          onClick={onToggleDesktopSidebar}
          aria-label={desktopCollapsed ? "展开侧栏" : "收起侧栏"}
          title={desktopCollapsed ? "展开侧栏" : "收起侧栏（全屏）"}
          className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 md:flex"
        >
          <PanelLeft className="h-[18px] w-[18px]" strokeWidth={2} />
        </button>
        <Breadcrumb />
      </div>

      <div className="hidden h-10 w-[400px] items-center gap-2.5 rounded-lg bg-slate-100 px-3.5 lg:flex">
        <Search className="h-4 w-4 shrink-0 text-slate-400" strokeWidth={2} />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submitSearch(); }}
          placeholder="搜索订单号 / 客户姓名…（回车）"
          className="flex-1 bg-transparent text-[13px] text-slate-900 placeholder:text-slate-400 outline-none"
        />
      </div>

      <div className="flex items-center gap-3">
        {/* ═ COMPANY-STRIP BEGIN ═ */}
        <SubscriptionTools />
        {/* ═ COMPANY-STRIP END ═ */}
        <NotificationBell />

        <button
          type="button"
          onClick={() => setLocale(locale === "zh" ? "en" : "zh")}
          className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3.5 text-[13px] font-medium text-slate-900 hover:bg-slate-50"
        >
          <Globe className="h-3.5 w-3.5 text-slate-500" strokeWidth={2} />
          <span>中 / EN</span>
        </button>

        {canSwitchRole && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setRoleMenuOpen((v) => !v)}
              className="flex h-9 items-center gap-1.5 rounded-lg border border-dashed border-amber-400 bg-amber-50 px-3 text-[12px] font-medium text-amber-700 hover:bg-amber-100"
            >
              <RefreshCw className="h-3.5 w-3.5" strokeWidth={2} />
              切角色
            </button>
            {roleMenuOpen && (
              <div className="absolute right-0 top-11 z-30 w-48 rounded-lg border border-slate-200 bg-white p-1 shadow-lg">
                {ALL_ROLES.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      switchRole(r);
                      setRoleMenuOpen(false);
                    }}
                    className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-[13px] hover:bg-slate-100"
                  >
                    <span>{ROLE_LABEL[r]}</span>
                    {r === user?.role && <span className="text-blue-600">·</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {user && <UserMenu />}
      </div>

    </header>
  );
}
